import { useState, useCallback, useEffect } from 'react';
import spotlightService from '../services/SpotlightService';
import { userDataService } from '../services/UserDataService';
import { useProfile } from '../contexts/ProfileContext';

/**
 * Custom hook for managing user reactions
 * Uses localStorage to persist reactions across component remounts
 */
export const useReactions = (channelId, initialReaction = null) => {
  const { currentProfile } = useProfile();
  
  // Get stored reaction from localStorage or use initialReaction
  const getStoredReaction = useCallback(() => {
    if (!channelId || !currentProfile?._id) {return initialReaction;}
    try {
      const key = `reaction_${currentProfile._id}_${channelId}`;
      const stored = localStorage.getItem(key);
      
      // If we have a localStorage entry, use it
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        
        // If it's our special "removed" marker, return null
        if (parsed === '__REMOVED__') {
          console.log('📖 useReactions: getStoredReaction (explicitly removed):', { key });
          return null;
        }
        
        // Otherwise return the stored reaction
        console.log('📖 useReactions: getStoredReaction (from localStorage):', { key, stored, result: parsed });
        return parsed;
      }
      
      // Only use initialReaction if we have NO localStorage entry at all
      console.log('📖 useReactions: getStoredReaction (using initialReaction):', { key, initialReaction });
      return initialReaction;
    } catch (e) {
      console.warn('Failed to parse stored reaction:', e);
      return initialReaction;
    }
  }, [channelId, initialReaction, currentProfile?._id]);

  const [currentReaction, setCurrentReaction] = useState(getStoredReaction);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update localStorage when reaction changes
  const updateStoredReaction = useCallback((reaction) => {
    if (!channelId || !currentProfile?._id) {return;}
    try {
      const key = `reaction_${currentProfile._id}_${channelId}`;
      console.log('💾 useReactions: updateStoredReaction:', { key, reaction });
      
      if (reaction === null) {
        // Store a special "removed" marker instead of removing the key
        // This prevents initialReaction from overriding our explicit removal
        localStorage.setItem(key, JSON.stringify('__REMOVED__'));
        console.log('💾 useReactions: Marked as removed in localStorage:', key);
      } else {
        localStorage.setItem(key, JSON.stringify(reaction));
        console.log('💾 useReactions: Stored to localStorage:', key, '=', reaction);
      }
      
      // Dispatch custom event to notify other components (like useMyReactions)
      // This is needed because storage events only fire across tabs, not within the same tab
      window.dispatchEvent(new CustomEvent('reactionChanged', {
        detail: {
          key,
          channelId,
          profileId: currentProfile._id,
          reaction,
        },
      }));
      console.log('📢 useReactions: Dispatched reactionChanged event');
      
      // Debug: Check what's actually in localStorage now
      const allReactionKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.includes(channelId)) {
          allReactionKeys.push({
            key: storageKey,
            value: localStorage.getItem(storageKey),
          });
        }
      }
      console.log('💾 useReactions: All localStorage keys for channel:', channelId, allReactionKeys);
    } catch (e) {
      console.warn('Failed to store reaction:', e);
    }
  }, [channelId, currentProfile?._id]);

  // Update state and localStorage
  const updateReaction = useCallback((reaction) => {
    console.log('🔄 useReactions: updateReaction called with:', reaction);
    console.log('🔄 useReactions: Previous reaction was:', currentReaction);
    setCurrentReaction(reaction);
    updateStoredReaction(reaction);
    console.log('🔄 useReactions: State should now be:', reaction);
  }, [updateStoredReaction, currentReaction]);

  // Initialize from localStorage on mount or when channelId changes
  useEffect(() => {
    const stored = getStoredReaction();
    if (stored !== currentReaction) {
      setCurrentReaction(stored);
    }
  }, [channelId, getStoredReaction, currentReaction]);

  /**
   * Create or update a reaction
   */
  const setReaction = useCallback(async (action) => {
    if (!channelId || isLoading) {
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // If clicking the same reaction, remove it
      if (currentReaction === action) {
        console.log('🔄 useReactions: Removing reaction, calling API...');
        console.log('🔄 useReactions: Current state before API call:', currentReaction);
        await spotlightService.removeUserChannelReaction(channelId);
        console.log('🔄 useReactions: API call succeeded, updating state to null...');
        updateReaction(null);
        console.log('🔄 useReactions: State updated to null');
        
        // When user removes their reaction completely, also remove from watchlist
        try {
          console.log('🚫 useReactions: User removed reaction, checking watchlist...', {
            channelId,
          });
          const isInWatchlist = userDataService.isInWatchlist(channelId);
          console.log('🚫 useReactions: Watchlist check result:', {
            channelId,
            isInWatchlist,
          });
          
          if (isInWatchlist) {
            console.log('🚫 useReactions: Removing from watchlist...');
            const removeResult = await userDataService.removeFromWatchlist(channelId);
            console.log('🚫 useReactions: Watchlist removal result:', removeResult);
          } else {
            console.log('🚫 useReactions: Content was not in watchlist, no action needed');
          }
        } catch (watchlistError) {
          console.error('🚫 useReactions: Failed to remove from watchlist:', watchlistError);
          // Don't fail the reaction if watchlist removal fails
        }
      } else {
        // Create or update reaction
        console.log('🔄 useReactions: Setting new reaction, calling API...');
        console.log('🔄 useReactions: Current state before API call:', currentReaction);
        console.log('🔄 useReactions: Target action:', action);
        await spotlightService.createOrUpdateUserReaction(channelId, action);
        console.log('🔄 useReactions: API call succeeded, updating state to:', action);
        updateReaction(action);
        console.log('🔄 useReactions: State updated to:', action);
        
        // If user dislikes content, also remove it from their watchlist
        if (action === 'dislike' || action === 'double_dislike') {
          try {
            console.log('👎 useReactions: User disliked content, checking watchlist...', {
              channelId,
              action,
            });
            const isInWatchlist = userDataService.isInWatchlist(channelId);
            console.log('👎 useReactions: Watchlist check result:', {
              channelId,
              isInWatchlist,
            });
            
            if (isInWatchlist) {
              console.log('👎 useReactions: Removing from watchlist...');
              const removeResult = await userDataService.removeFromWatchlist(channelId);
              console.log('👎 useReactions: Watchlist removal result:', removeResult);
            } else {
              console.log('👎 useReactions: Content was not in watchlist, no action needed');
            }
          } catch (watchlistError) {
            console.error('👎 useReactions: Failed to remove from watchlist:', watchlistError);
            // Don't fail the reaction if watchlist removal fails
          }
        }
      }
      return true;
    } catch (error) {
      console.error('🔄 useReactions: Failed to update reaction:', error);
      console.error('🔄 useReactions: Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        channelId,
        action,
        currentReaction,
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to update reaction';
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        errorMessage = 'Please log in to rate content';
      } else if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
        errorMessage = 'You don\'t have permission to rate this content';
      } else if (error.message?.includes('404')) {
        errorMessage = 'Content not found';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error - please try again';
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [channelId, currentReaction, isLoading, updateReaction]);

  /**
   * Remove current reaction
   */
  const removeReaction = useCallback(async () => {
    if (!channelId || isLoading || !currentReaction) {return false;}

    try {
      setIsLoading(true);
      setError(null);

      await spotlightService.removeUserChannelReaction(channelId);
      updateReaction(null);
      
      return true;
    } catch (error) {
      console.error('Failed to remove reaction:', error);
      setError(error.message || 'Failed to remove reaction');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [channelId, currentReaction, isLoading, updateReaction]);

  return {
    currentReaction,
    isLoading,
    error,
    setReaction,
    removeReaction,
    hasReaction: !!currentReaction,
  };
};

export default useReactions;